import React, { useState } from "react";
import PropTypes from "prop-types";

const NameInput = ({
  title,
  placeholder,
  width = "400px",
  value,
  onChange,
  validationMessage = "This field is required.",
  iconColor = "#EF4444",
  iconSize = "16px",
  apiEndpoint,
  bgColorLight = "#ffffff",
  bgColorDark = "#1E1E1E",
  isDarkMode = false,
}) => {
  const [error, setError] = useState("");

  const handleBlur = () => {
    if (!value.trim()) {
      setError(validationMessage);
    } else {
      setError("");
      if (apiEndpoint) {
        // Optional API call on blur
        fetch(apiEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: value }),
        })
          .then(() => console.log("Data saved successfully."))
          .catch((err) => console.error("Error saving data:", err));
      }
    }
  };

  const handleFocus = () => {
    if (error) setError("");
  };

  return (
    <>
      <style>{`
        /* These styles must be defined globally, but we include them here for completeness.
           If already defined in a parent (e.g., LoginPage), they will be reused. */
        .dd-input-group { display: flex; flex-direction: column; gap: 6px; }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 6px;
          display: block;
        }

        .dd-required { color: #EF4444; margin-left: 2px; }

        .dd-input-wrapper { position: relative; width: 100%; }

        .dd-input {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }

        .dd-input.dd-input-error {
          border-color: #EF4444;
          padding-right: 36px;
        }
        .dd-input.dd-input-error:focus {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }

        .dd-error-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #EF4444;
          display: flex;
        }

        .dd-error-msg {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #EF4444;
          margin-top: 4px;
        }
      `}</style>

      <div className="dd-input-group" style={{ maxWidth: width }}>
        <label className="dd-section-label">
          {title} <span className="dd-required">*</span>
        </label>

        <div className="dd-input-wrapper">
          <input
            className={`dd-input ${error ? "dd-input-error" : ""}`}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            style={{
              backgroundColor: isDarkMode ? bgColorDark : bgColorLight,
              color: isDarkMode ? "#FFFFFF" : "#0F172A",
            }}
          />
          {error && (
            <span className="dd-error-icon" title={error}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                />
                <path
                  d="M12 8v5M11.995 16h.009"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>

        {error && (
          <div className="dd-error-msg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
                stroke="#EF4444"
                strokeWidth="1.5"
              />
              <path
                d="M12 8v5M11.995 16h.009"
                stroke="#EF4444"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {error}
          </div>
        )}
      </div>
    </>
  );
};

NameInput.propTypes = {
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  width: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  validationMessage: PropTypes.string,
  iconColor: PropTypes.string,
  iconSize: PropTypes.string,
  apiEndpoint: PropTypes.string,
  bgColorLight: PropTypes.string,
  bgColorDark: PropTypes.string,
  isDarkMode: PropTypes.bool,
};

export default NameInput;
